import type { ReactNode } from 'react';
import { Label } from '@/components/ui/label';
import {
  chileDateToDateTimeLocal,
  dateTimeLocalToChileDate,
} from '@/lib/timezone';
import type { Event } from '@/types';

// The shape both event dialogs hold. Every value is a string (or a checkbox boolean) so
// the inputs stay controlled; `toPayload` turns it into what the controller permits —
// which is the same conversion `dateTimeLocalToChileDate` did inside the server actions.
export type EventFormData = {
  name: string;
  slug: string;
  domain: string;
  photosAlbumUrl: string;
  youtubeLiveUrl: string;
  priorityAnswerDate: string;
  priorityDeadlineAt: string;
  finalDeadlineAt: string;
  startsAt: string;
  endsAt: string;
  hackingEndsAt: string;
  rsvpOpenAt: string;
  votingStartsAt: string;
  votingEndsAt: string;
  trackSelectionStartTime: string;
  trackSelectionEndTime: string;
  mentorSelectionStartTime: string;
  mentorSelectionEndTime: string;
  feedbackPrizeDeadline: string;
  capacityTeams: string;
  capacityHackers: string;
  targetSubmission: string;
  trackTeamLimit: string;
  mentorTeamLimit: string;
  autoAssignReviewers: boolean;
  discordMessagesEnabled: boolean;
  discordGuildId: string;
  discordWelcomeChannelId: string;
  discordFaqChannelId: string;
  discordMusicChannelId: string;
  arcadePhysicalKeyMap: Record<string, string> | null;
};

const DATE_FIELDS = [
  'priorityAnswerDate',
  'priorityDeadlineAt',
  'finalDeadlineAt',
  'startsAt',
  'endsAt',
  'hackingEndsAt',
  'rsvpOpenAt',
  'votingStartsAt',
  'votingEndsAt',
  'trackSelectionStartTime',
  'trackSelectionEndTime',
  'mentorSelectionStartTime',
  'mentorSelectionEndTime',
  'feedbackPrizeDeadline',
] as const;

const NUMBER_FIELDS = [
  'capacityTeams',
  'capacityHackers',
  'targetSubmission',
  'trackTeamLimit',
  'mentorTeamLimit',
] as const;

export const emptyEventForm: EventFormData = {
  name: '',
  slug: '',
  domain: 'https://hack.platan.us',
  photosAlbumUrl: '',
  youtubeLiveUrl: '',
  priorityAnswerDate: '',
  priorityDeadlineAt: '',
  finalDeadlineAt: '',
  startsAt: '',
  endsAt: '',
  hackingEndsAt: '',
  rsvpOpenAt: '',
  votingStartsAt: '',
  votingEndsAt: '',
  trackSelectionStartTime: '',
  trackSelectionEndTime: '',
  mentorSelectionStartTime: '',
  mentorSelectionEndTime: '',
  feedbackPrizeDeadline: '',
  capacityTeams: '',
  capacityHackers: '',
  targetSubmission: '',
  trackTeamLimit: '',
  mentorTeamLimit: '',
  autoAssignReviewers: false,
  discordMessagesEnabled: true,
  discordGuildId: '',
  discordWelcomeChannelId: '',
  discordFaqChannelId: '',
  discordMusicChannelId: '',
  arcadePhysicalKeyMap: null,
};

// The columns are instants; the inputs show Santiago wall-clock, exactly as the Next
// update dialog did with `chileDateToDateTimeLocal`.
function toDateTimeLocal(value: string | null): string {
  return chileDateToDateTimeLocal(value ? new Date(value) : null);
}

function toNumberInput(value: number | null): string {
  return value === null ? '' : String(value);
}

export function eventToForm(event: Event): EventFormData {
  return {
    name: event.name,
    slug: event.slug,
    domain: event.domain,
    photosAlbumUrl: event.photosAlbumUrl ?? '',
    youtubeLiveUrl: event.youtubeLiveUrl ?? '',
    priorityAnswerDate: toDateTimeLocal(event.priorityAnswerDate),
    priorityDeadlineAt: toDateTimeLocal(event.priorityDeadlineAt),
    finalDeadlineAt: toDateTimeLocal(event.finalDeadlineAt),
    startsAt: toDateTimeLocal(event.startsAt),
    endsAt: toDateTimeLocal(event.endsAt),
    hackingEndsAt: toDateTimeLocal(event.hackingEndsAt),
    rsvpOpenAt: toDateTimeLocal(event.rsvpOpenAt),
    votingStartsAt: toDateTimeLocal(event.votingStartsAt),
    votingEndsAt: toDateTimeLocal(event.votingEndsAt),
    trackSelectionStartTime: toDateTimeLocal(event.trackSelectionStartTime),
    trackSelectionEndTime: toDateTimeLocal(event.trackSelectionEndTime),
    mentorSelectionStartTime: toDateTimeLocal(event.mentorSelectionStartTime),
    mentorSelectionEndTime: toDateTimeLocal(event.mentorSelectionEndTime),
    feedbackPrizeDeadline: toDateTimeLocal(event.feedbackPrizeDeadline),
    capacityTeams: toNumberInput(event.capacityTeams),
    capacityHackers: toNumberInput(event.capacityHackers),
    targetSubmission: toNumberInput(event.targetSubmission),
    trackTeamLimit: toNumberInput(event.trackTeamLimit),
    mentorTeamLimit: toNumberInput(event.mentorTeamLimit),
    autoAssignReviewers: event.autoAssignReviewers,
    discordMessagesEnabled: event.discordMessagesEnabled,
    discordGuildId: event.discordGuildId ?? '',
    discordWelcomeChannelId: event.discordWelcomeChannelId ?? '',
    discordFaqChannelId: event.discordFaqChannelId ?? '',
    discordMusicChannelId: event.discordMusicChannelId ?? '',
    arcadePhysicalKeyMap: event.arcadePhysicalKeyMap,
  };
}

export function toPayload(data: EventFormData): Record<string, unknown> {
  const payload: Record<string, unknown> = { ...data };

  for (const field of DATE_FIELDS) {
    payload[field] =
      dateTimeLocalToChileDate(data[field] || undefined)?.toISOString() ?? null;
  }
  for (const field of NUMBER_FIELDS) {
    payload[field] = data[field] === '' ? null : Number(data[field]);
  }

  return payload;
}

// The Next dialogs used shadcn's react-hook-form wrappers, which are deliberately not
// ported (U2). This reproduces the markup FormItem/FormLabel/FormMessage rendered.
export function Field({
  label,
  htmlFor,
  error,
  help,
  children,
}: {
  label?: string;
  htmlFor?: string;
  error?: string;
  help?: string;
  children: ReactNode;
}) {
  return (
    <div className="grid gap-2">
      {label && (
        <Label htmlFor={htmlFor} data-error={error ? 'true' : undefined}>
          {label}
        </Label>
      )}
      {children}
      {help && <p className="text-muted-foreground text-xs">{help}</p>}
      {error && <p className="text-destructive text-sm">{error}</p>}
    </div>
  );
}
